<script lang="ts">
  import { fade, fly } from 'svelte/transition';
  import { portal } from 'svelte-portal';
  import { dims } from '~/utils/dimensions.svelte';
  import ScrollShadow from './ScrollShadow.svelte';
  import { navigatingPage } from '~/lib/nav.svelte';
  import type { Snippet } from 'svelte';

  type Props = {
    id?: string;
    anchor?: string;
    virtual?: boolean;
    fullHeight?: boolean;
    index?: number;
    open: boolean;
    onClose: undefined | (() => void);
    contentHeight?: number;
    alignTop?: boolean;
    hideContent?: boolean;
    title: Snippet;
    content?: Snippet<
      [
        {
          scrollTo: (pos: number) => void;
          scrollOffset: number;
          contentHeight: number;
        },
      ]
    >;
    controls: Snippet;
  };

  let { contentHeight = $bindable(), ...props }: Props = $props();

  let content: HTMLDivElement | undefined = $state();
  let scrollOffset = $state(0);

  $effect(() => {
    if (props.open && content) {
      scrollOffset = content.scrollTop;
    }
  });

  const handleScroll = (e: UIEvent) => {
    scrollOffset = (e.target as HTMLElement).scrollTop;
  };

  const scrollTo = (pos: number) => {
    content?.scrollTo({ top: pos, behavior: 'smooth' });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.open && e.key === 'Escape') {
      props.onClose?.();
    }
  };

  const handleClose = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      props.onClose?.();
    }
  };
</script>

<svelte:window on:keydown={handleKeyDown} />
{#if props.open && !navigatingPage.current}
  <div
    use:portal={props.anchor ?? 'body'}
    class="container"
    class:align-top={props.alignTop}
    style="
    --padding: {dims.pad}px;
    --z-index: calc({props.index ?? 1} * 1000)
    "
  >
    <div
      class="backdrop"
      transition:fade
      onclick={handleClose}
      onkeydown={handleKeyDown}
      role="presentation"
    ></div>
    <div
      id={props.id}
      transition:fly|global={{ y: 100, duration: 250 }}
      class="panel"
      class:full-height={props.fullHeight}
    >
      <h1 class="title">
        {@render props.title()}
      </h1>
      {#if !props.hideContent}
        <ScrollShadow>
          <div
            class="content"
            bind:this={content}
            bind:clientHeight={contentHeight}
            onscroll={handleScroll}
            class:virtual={props.virtual}
          >
            {@render props.content?.({ scrollTo, scrollOffset, contentHeight })}
          </div>
        </ScrollShadow>
      {/if}
      <div class="controls">
        {@render props.controls()}
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  @use '~/theme/include-media' as *;

  .container {
    pointer-events: none;
    z-index: calc(var(--z-index));
    position: absolute;
    overflow: hidden;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    &.align-top {
      padding-top: calc(var(--padding) + 48px);
      align-items: start;
    }
  }
  .backdrop {
    pointer-events: all;
    position: absolute;
    top: 0;
    right: 0;
    background-color: #00000040;
    width: 100%;
    height: 100%;
  }
  .title {
    width: 100%;
    padding: 0 0.5em;
    display: flex;
    gap: 0.5em;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    text-align: center;
    :global(svg) {
      font-size: 1em;
    }
  }
  .controls {
    display: flex;
    padding: 0.5em;
    gap: 0.5em;
  }
  .panel {
    transition-property: color, background-color;
    transition-duration: 0.25s;
    transition-timing-function: ease;
    pointer-events: all;
    z-index: calc(var(--z-index) + 1);
    background-color: white;
    border-radius: 4px;
    box-shadow:
      0 4px 8px rgba(0, 0, 0, 0.2),
      0 8px 16px rgba(0, 0, 0, 0.1);
    max-height: calc(100% - 2em);
    margin: 1em;
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    overflow: hidden;
    &.full-height {
      height: 100%;
    }
  }
  .content {
    height: 100%;
    scroll-behavior: smooth;
    scroll-padding: 4em;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1em;

    &.virtual {
      padding: 0;
      overflow: hidden;
    }
  }

  :global(html.dark-mode) .panel {
    background-color: #323438;
    color: white;
  }
  @include media('>600px') {
    .panel {
      width: 32em;
    }
  }

  @include media('>1200px') {
    .panel {
      width: 32em;
    }
  }
</style>
